import React from 'react';
import { useTranslation } from 'react-i18next';
import { Home, DollarSign, Users, Shield, TrendingUp, type LucideIcon } from 'lucide-react';
import { NeighborhoodData } from '../types';

interface NeighborhoodInfoProps {
  data: NeighborhoodData;
}

const NeighborhoodInfo: React.FC<NeighborhoodInfoProps> = ({ data }) => {
  const { t } = useTranslation();

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getCrimeColor = (crimeRate: string): string => {
    switch (crimeRate) {
      case 'Low':
        return 'text-green-600 bg-green-100';
      case 'Medium':
        return 'text-yellow-600 bg-yellow-100';
      case 'High':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getCrimeDescription = (crimeRate: string): string => {
    switch (crimeRate) {
      case 'Low':
        return t('neighborhood.crimeRateLow');
      case 'Medium':
        return t('neighborhood.crimeRateMedium');
      case 'High':
        return t('neighborhood.crimeRateHigh');
      default:
        return '';
    }
  };

  const StatCard = ({ icon: Icon, label, value, sublabel }: { icon: LucideIcon; label: string; value: string; sublabel?: string }) => (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Icon className="h-5 w-5 text-blue-600" />
        </div>
        <div className="flex-1">
          <div className="text-sm text-gray-600 mb-1">{label}</div>
          <div className="text-xl font-bold text-gray-900">{value}</div>
          {sublabel && <div className="text-xs text-gray-500 mt-1">{sublabel}</div>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('neighborhood.overview')}</h2>

      {/* Market Data */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <StatCard
          icon={Home}
          label={t('common.medianHomeValue')}
          value={formatCurrency(data.medianHomeValue)}
          sublabel={t('neighborhood.inThisNeighborhood')}
        />
        <StatCard
          icon={DollarSign}
          label={t('common.medianRent')}
          value={`${formatCurrency(data.medianRent)}/mo`}
          sublabel={t('neighborhood.averageMonthlyRent')}
        />
      </div>

      {/* Demographics */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-600" />
          {t('neighborhood.demographics')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">{t('common.population')}</div>
            <div className="text-2xl font-bold text-gray-900">
              {data.demographics.population.toLocaleString()}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">{t('common.medianAge')}</div>
            <div className="text-2xl font-bold text-gray-900">
              {data.demographics.medianAge}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">{t('common.medianIncome')}</div>
            <div className="text-2xl font-bold text-gray-900">
              {formatCurrency(data.demographics.medianIncome)}
            </div>
          </div>
        </div>
      </div>

      {/* Crime Rate */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Shield className="h-5 w-5 text-blue-600" />
          {t('neighborhood.safety')}
        </h3>
        <div className="flex items-center gap-3">
          <span className={`px-4 py-2 rounded-full font-semibold ${getCrimeColor(data.crimeRate)}`}>
            {data.crimeRate} {t('common.crimeRate')}
          </span>
          <span className="text-sm text-gray-600">
            {getCrimeDescription(data.crimeRate)}
          </span>
        </div>
      </div>

      <div className="pt-4 border-t border-gray-200">
        <div className="flex items-start gap-2 text-xs text-gray-500">
          <TrendingUp className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <p>
            {t('neighborhood.disclaimer')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default NeighborhoodInfo;
